/**
 * 📊 ImpactTicker — thin live "pulse" strip that rides just under the Hero.
 *
 * Pulls anonymized aggregates from `/api/v1/public/community-impact`
 * (Wave 1) and `/api/v1/public/roadmap` (Wave 4) and cycles through
 * three headline metrics in a horizontal reel:
 *
 *   1. "$X raised · N residents active"
 *   2. "N graduates · N labor hours reused"
 *   3. "N partner orgs · N donors"
 *
 * The number crawls up from 0 to N on first paint (300 ms per metric)
 * to give donors that immediate "pulse of the mission" feel without
 * being loud. Fully headless — silent-fails when the API is offline
 * so the homepage never breaks.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Heart, Users, Award, Briefcase, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;
const REEL_MS = 5500;      // How long each headline sits before we swap
const CRAWL_MS = 700;      // Duration of the count-up animation


function useCrawlValue(target, active) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) { setV(target || 0); return; }
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const to = Number(target || 0);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / CRAWL_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active]);
  return v;
}


export default function ImpactTicker() {
  const [impact, setImpact]     = useState(null);
  const [roadmap, setRoadmap]   = useState(null);
  const [reelIdx, setReelIdx]   = useState(0);
  const [firstPaint, setFP]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/v1/public/community-impact`, { withCredentials: false }).catch(() => null),
      axios.get(`${API}/v1/public/roadmap`,          { withCredentials: false }).catch(() => null),
    ]).then(([i, r]) => {
      if (cancelled) return;
      if (i?.data) setImpact(i.data);
      if (r?.data) setRoadmap(r.data);
      // Trigger the crawl once numbers land
      requestAnimationFrame(() => setFP(true));
    });
    return () => { cancelled = true; };
  }, []);

  const reels = useMemo(() => {
    // Impact payload uses nested groups — pluck the right subtrees.
    const impCost   = impact?.cost_avoidance || {};
    const impHouse  = impact?.residents_housed || {};
    const impWFP    = impact?.workforce_pipeline || {};
    const impLabor  = impact?.labor || {};
    const impPH     = impact?.program_hours || {};
    const rmTot     = roadmap?.totals || {};

    const costAvoidance   = impCost.total ?? 0;
    const activeResidents = impHouse.active ?? rmTot.residents ?? 0;
    const graduates       = impCost.graduates ?? rmTot.graduates ?? 0;
    const workforcePipeline = impWFP.active_pipeline ?? 0;
    const laborHours      = impLabor.labor_hours_dispatched ?? rmTot.labor_hours ?? 0;
    const wagesEarned     = impLabor.wages_earned_usd ?? 0;
    const partners        = rmTot.partners ?? 0;
    const donors          = rmTot.donors ?? 0;

    // Program hours — 1 real-time note = 1 hour served.
    const caseHours     = impPH.case_management_hours ?? 0;
    const lifeHours     = impPH.life_skills_hours ?? 0;
    const checkupHours  = impPH.residential_checkup_hours ?? 0;
    const programHoursTotal = impPH.total_hours ?? (caseHours + lifeHours + checkupHours);

    return [
      {
        id: "donation-pulse",
        parts: [
          { key: "avoided", label: "community cost avoided", value: costAvoidance, fmt: (n) => "$" + n.toLocaleString(), icon: <Heart className="h-3.5 w-3.5" /> },
          { key: "active",  label: "residents active",        value: activeResidents, fmt: (n) => n.toLocaleString(), icon: <Users className="h-3.5 w-3.5" /> },
        ],
      },
      {
        id: "care-pulse",
        parts: [
          { key: "care_hours",   label: "hours of care logged",   value: programHoursTotal, fmt: (n) => n.toLocaleString(), icon: <Heart className="h-3.5 w-3.5" /> },
          { key: "case_hours",   label: "case-management notes",  value: caseHours, fmt: (n) => n.toLocaleString(), icon: <Sparkles className="h-3.5 w-3.5" /> },
        ],
      },
      {
        id: "workforce-pulse",
        parts: [
          { key: "hours",    label: "labor hours dispatched", value: laborHours, fmt: (n) => n.toLocaleString(), icon: <Briefcase className="h-3.5 w-3.5" /> },
          { key: "wages",    label: "wages earned",           value: wagesEarned, fmt: (n) => "$" + n.toLocaleString(), icon: <Sparkles className="h-3.5 w-3.5" /> },
        ],
      },
      {
        id: "outcome-pulse",
        parts: [
          { key: "grads",    label: "graduates",              value: graduates, fmt: (n) => n.toLocaleString(), icon: <Award className="h-3.5 w-3.5" /> },
          { key: "pipeline", label: "workforce pipeline",     value: workforcePipeline, fmt: (n) => n.toLocaleString(), icon: <Users className="h-3.5 w-3.5" /> },
        ],
      },
      {
        id: "alliance-pulse",
        parts: [
          { key: "partners", label: "partner orgs",           value: partners, fmt: (n) => n.toLocaleString(), icon: <Handshake className="h-3.5 w-3.5" /> },
          { key: "donors",   label: "donors",                 value: donors, fmt: (n) => n.toLocaleString(), icon: <Heart className="h-3.5 w-3.5" /> },
        ],
      },
    ];
  }, [impact, roadmap]);

  useEffect(() => {
    if (!reels.length) return;
    const t = setInterval(() => setReelIdx((i) => (i + 1) % reels.length), REEL_MS);
    return () => clearInterval(t);
  }, [reels.length]);

  const active = reels[reelIdx];

  return (
    <div
      className="w-full bg-[#0A0612] text-[#F4D88A] border-y border-[#F4D88A]/15 overflow-hidden"
      data-testid="impact-ticker"
      aria-label="Live impact ticker"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 py-3 md:py-4">
        <div className="flex items-center gap-4 md:gap-6 flex-wrap md:flex-nowrap">
          <div className="flex items-center gap-2 font-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[#F4D88A]/70 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            Live Pulse
          </div>

          {active && (
            <div className="flex items-center gap-5 md:gap-8 flex-wrap md:flex-nowrap flex-1" data-testid={`ticker-reel-${active.id}`}>
              {active.parts.map((p, idx) => (
                <TickerCell key={p.key} part={p} active={firstPaint} />
              )).reduce((acc, el, i) => {
                if (i > 0) acc.push(<span key={`sep-${i}`} className="text-[#F4D88A]/30 hidden md:inline">·</span>);
                acc.push(el);
                return acc;
              }, [])}
            </div>
          )}

          {reels.length > 1 && (
            <div className="flex gap-1.5 ml-auto" data-testid="ticker-dots">
              {reels.map((r, i) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setReelIdx(i)}
                  aria-label={`Ticker reel ${i + 1}`}
                  data-testid={`ticker-dot-${r.id}`}
                  className={`h-1.5 w-1.5 rounded-full transition-all ${
                    i === reelIdx ? "bg-[#F4D88A] w-6" : "bg-[#F4D88A]/25 hover:bg-[#F4D88A]/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function TickerCell({ part, active }) {
  const v = useCrawlValue(part.value, active);
  return (
    <div className="flex items-baseline gap-2 min-w-0" data-testid={`ticker-cell-${part.key}`}>
      <span className="text-[#C9A227] shrink-0 self-center">{part.icon}</span>
      <span className="font-display text-xl md:text-2xl font-bold text-white tabular-nums" data-testid={`ticker-value-${part.key}`}>
        {part.fmt(v)}
      </span>
      <span className="font-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase text-[#F4D88A]/70 whitespace-nowrap">
        {part.label}
      </span>
    </div>
  );
}
